import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DaySummary } from "../lib/api";

type Props = {
  days: DaySummary[];
  goal: { calories: number; protein_g: number };
  tz: string;
};

function dayLabel(day: string, tz: string): string {
  const d = new Date(`${day}T12:00:00`);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: tz,
  }).format(d);
}

export function TrendChart({ days, goal, tz }: Props) {
  const data = days.map((d) => ({
    label: dayLabel(d.day, tz),
    calories: d.meals > 0 ? d.calories : null,
    protein_g: d.meals > 0 ? d.protein_g : null,
  }));

  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
          <XAxis dataKey="label" stroke="var(--muted)" interval={4} />
          <YAxis
            yAxisId="left"
            stroke="var(--muted)"
            domain={[0, (dataMax: number) => Math.max(dataMax, goal.calories) * 1.1]}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="var(--muted)"
            domain={[0, (dataMax: number) => Math.max(dataMax, goal.protein_g) * 1.1]}
          />
          <Tooltip
            contentStyle={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
          <Legend />
          <ReferenceLine
            yAxisId="left"
            y={goal.calories}
            stroke="var(--accent-cal)"
            strokeDasharray="4 4"
            label={{ value: "kcal goal", fill: "var(--muted)", fontSize: 10, position: "insideTopRight" }}
          />
          <ReferenceLine
            yAxisId="right"
            y={goal.protein_g}
            stroke="var(--accent-pro)"
            strokeDasharray="4 4"
            label={{ value: "protein goal", fill: "var(--muted)", fontSize: 10, position: "insideBottomLeft" }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="calories"
            name="Calories"
            stroke="var(--accent-cal)"
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls={false}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="protein_g"
            name="Protein (g)"
            stroke="var(--accent-pro)"
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
